import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { BookOpen, Sparkles, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { readInterviewPrep, type InterviewPrepData } from "./interviewPrep/readInterviewPrep";
import { InterviewPrepHeader } from "./interviewPrep/InterviewPrepHeader";
import { InterviewPrepContext } from "./interviewPrep/InterviewPrepContext";
import { InterviewPrepQuestions } from "./interviewPrep/InterviewPrepQuestions";
import { InterviewPrepEvents } from "./interviewPrep/InterviewPrepEvents";
import { useMatchInterviewPrep, getQuestionNoteValue } from "./interviewPrep/useMatchInterviewPrep";
import { useMatchInterviewEvents } from "./interviewPrep/useMatchInterviewEvents";

interface InterviewPrepProps {
	matchId: string | undefined;
}

interface MatchRow {
	id: string;
	ai_analysis: unknown;
	updated_at: string | null;
}

const EmptyState = ({
	icon,
	title,
	description,
}: {
	icon: React.ReactNode;
	title: string;
	description: string;
}) => (
	<div className="flex flex-col items-center justify-center p-12 text-center">
		<div className="flex h-16 w-16 items-center justify-center rounded-sm border border-border bg-card mb-6">{icon}</div>
		<h3 className="text-xl font-semibold text-foreground mb-3">{title}</h3>
		<p className="text-sm text-muted-foreground max-w-md leading-relaxed">{description}</p>
	</div>
);

const LoadingSkeleton = () => (
	<div className="space-y-4">
		<Skeleton className="h-14 w-full" />
		<Skeleton className="h-24 w-full" />
		<div className="grid md:grid-cols-2 gap-4">
			<Skeleton className="h-32 w-full" />
			<Skeleton className="h-32 w-full" />
		</div>
		<Skeleton className="h-64 w-full" />
	</div>
);

export const InterviewPrep = ({ matchId }: InterviewPrepProps) => {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [match, setMatch] = useState<MatchRow | null>(null);
	const [matchLoading, setMatchLoading] = useState(true);
	const [isReAnalyzing, setIsReAnalyzing] = useState(false);

	// Load the match (only the fields we need)
	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			if (!matchId) {
				setMatchLoading(false);
				return;
			}
			setMatchLoading(true);
			const { data, error } = await supabase
				.from("matches")
				.select("id, ai_analysis, updated_at")
				.eq("id", matchId)
				.single();
			if (cancelled) return;
			if (error) {
				console.error("Error loading match for interview prep:", error);
				setMatch(null);
			} else {
				setMatch(data);
			}
			setMatchLoading(false);
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [matchId]);

	// Live prep data — always reflects the current ai_analysis.
	const livePrepData: InterviewPrepData = useMemo(
		() => readInterviewPrep(match),
		[match]
	);

	const {
		loading: notesLoading,
		saveState,
		notes,
		generalNotes,
		questionsSnapshot,
		updatedAt: interviewPrepUpdatedAt,
		setActiveQuestions,
		regenerateQuestions,
		loadMoreQuestions,
		ignoreQuestion,
		deleteQuestion,
		updateQuestionNote,
		updateGeneralNotes,
	} = useMatchInterviewPrep(matchId);

	// Keep the hook aware of the live question list so it can seed the
	// snapshot on the first write.
	useEffect(() => {
		setActiveQuestions(livePrepData.questions);
	}, [livePrepData.questions, setActiveQuestions]);

	// Effective prep data: context from the live analysis, questions frozen
	// from the snapshot if one exists.
	const prepData: InterviewPrepData = useMemo(() => {
		if (questionsSnapshot && questionsSnapshot.length > 0)
			return { ...livePrepData, questions: questionsSnapshot };
		return livePrepData;
	}, [livePrepData, questionsSnapshot]);

	const handleLoadMore = () => {
		void loadMoreQuestions(livePrepData.questions);
	};

	const handleReAnalyze = async () => {
		if (!matchId) return;
		setIsReAnalyzing(true);
		try {
			const { error } = await supabase.functions.invoke('ai-processing?command=push-new-analysis', {
				method: 'POST',
				body: {
					entityId: matchId,
					entityType: "match",
					analysisType: "full-match",
					actionOnComplete: "update-match",
				}
			});
			if (error) throw error;
			toast({
				title: t('common.success'),
				description: t('matching.interviewPrep.reAnalyze.success', 'Analysis re-queued — new questions will appear shortly.'),
			});
		} catch {
			toast({
				title: t('common.error', 'Error'),
				description: t('matching.interviewPrep.reAnalyze.error', 'Failed to trigger re-analysis. Please try again.'),
				variant: "destructive",
			});
		} finally {
			setIsReAnalyzing(false);
		}
	};

	// Show the re-analyze button when interview prep notes are newer than the last analysis.
	const matchUpdatedAt = match?.updated_at ? new Date(match.updated_at) : null;
	const prepUpdatedAt = interviewPrepUpdatedAt ? new Date(interviewPrepUpdatedAt) : null;
	const showReAnalyze = !!(prepUpdatedAt && matchUpdatedAt && prepUpdatedAt > matchUpdatedAt);

	// Questions whose TEXT does not already exist in the snapshot are "new",
	// regardless of key — the AI always reuses question1…question10 as keys
	// but generates different text on every re-run.
	const snapshotTexts = new Set((questionsSnapshot || []).map((q) => q.text));
	const newQuestionsCount = questionsSnapshot
		? livePrepData.questions.filter((q) => !snapshotTexts.has(q.text)).length
		: 0;

	const { upcoming, past } = useMatchInterviewEvents(matchId);

	// Loading state: wait for the match itself; notes load in parallel
	if (matchLoading || notesLoading)
		return <LoadingSkeleton />;

	// Empty state 1: no ai_analysis at all
	if (!prepData.hasAnalysis)
		return (
			<EmptyState
				icon={<Sparkles className="h-7 w-7 text-muted-foreground" />}
				title={t("matching.interviewPrep.empty.noAnalysis.title", "Run match analysis first")}
				description={t(
					"matching.interviewPrep.empty.noAnalysis.description",
					"Interview questions are generated as part of the match analysis. Run it from the match overview to unlock the interview prep workspace."
				)}
			/>
		);

	// Empty state 2: ai_analysis present but no questions (older analyses)
	if (prepData.questions.length === 0)
		return (
			<div>
				<InterviewPrepHeader
					overallScore={prepData.overallScore}
					answeredCount={0}
					totalQuestions={0}
					saveState={saveState}
				/>
				<InterviewPrepContext
					recommendation={prepData.recommendation}
					strengths={prepData.strengths}
					gaps={prepData.gaps}
					categories={prepData.categories}
				/>
				<Card className="border-dashed bg-muted/30">
					<CardContent className="py-8">
						<EmptyState
							icon={<BookOpen className="h-7 w-7 text-muted-foreground" />}
							title={t(
								"matching.interviewPrep.empty.noQuestions.title",
								"No interview questions yet"
							)}
							description={t(
								"matching.interviewPrep.empty.noQuestions.description",
								"This match was analyzed before interview questions were generated. Re-run the analysis to produce a fresh set of tailored questions."
							)}
						/>
					</CardContent>
				</Card>
			</div>
		);

	const rawQuestionNotes = notes.questions || {};
	const questionNotesByKey: Record<string, string> = {};
	for (const key of Object.keys(rawQuestionNotes))
		questionNotesByKey[key] = getQuestionNoteValue(rawQuestionNotes[key]);
	const answeredCount = prepData.questions.filter(
		(q) => (questionNotesByKey[q.key] || "").trim().length > 0
	).length;

	return (
		<div>
			<InterviewPrepHeader
				overallScore={prepData.overallScore}
				answeredCount={answeredCount}
				totalQuestions={prepData.questions.length}
				saveState={saveState}
			/>

			<InterviewPrepEvents upcoming={upcoming} past={past} />

			<InterviewPrepContext
				recommendation={prepData.recommendation}
				strengths={prepData.strengths}
				gaps={prepData.gaps}
				categories={prepData.categories}
			/>

			<InterviewPrepQuestions
				questions={prepData.questions}
				notesByKey={questionNotesByKey}
				onChangeNote={updateQuestionNote}
				onIgnore={ignoreQuestion}
				onDelete={deleteQuestion}
				onLoadMore={questionsSnapshot !== null && answeredCount > 0 ? handleLoadMore : undefined}
				newQuestionsCount={newQuestionsCount}
				onReAnalyze={showReAnalyze ? handleReAnalyze : undefined}
				isReAnalyzing={isReAnalyzing}
				onRegenerate={
					questionsSnapshot !== null && newQuestionsCount > 0
						? () => void regenerateQuestions(livePrepData.questions)
						: undefined
				}
			/>

			<Card>
				<CardContent className="p-4 space-y-2">
					<label className="text-sm font-semibold text-foreground flex items-center gap-2">
						<FileText className="h-4 w-4 text-muted-foreground" />
						{t("matching.interviewPrep.generalNotes", "General notes")}
					</label>
					<Textarea
						placeholder={t(
							"matching.interviewPrep.generalNotesPlaceholder",
							"Anything else you want to remember going into this interview…"
						)}
						value={generalNotes}
						onChange={(e) => updateGeneralNotes(e.target.value)}
						className="min-h-[120px] resize-y"
						rows={4}
					/>
				</CardContent>
			</Card>
		</div>
	);
};
